import { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { Spinner, Stack, Text, XStack, YStack } from "tamagui";
import { RiBankCardLine, RiCheckLine, RiCloseLine } from "react-icons/ri";
import { OwnedPaymentMethod } from "@screen/Home/pages/Account/API/handlePaymentAPI";
import type { PaymentSelection } from "@utils/store/cart/useOrder";
import InlineBindCardForm, { BoundCard } from "./InlineBindCardForm";
import { WALLET_BRANDS } from "../utils/paymentSelection";
import { TAPPAY_LOGO_URL, TAPPAY_SECURITY_COPY_LEAD, TAPPAY_SECURITY_COPY_REST } from "@components/payment/TapPayCompliance";
import "../gasPayment.scss";
import type { WalletBrand } from "@utils/store/cart/useOrder";

const applePayLogo = require("@styles/images/payment/apple_pay.png") as string;
const linePayLogo = require("@styles/images/payment/line_pay.png") as string;
const ipassMoneyLogo = require("@styles/images/payment/ipass_money.png") as string;

/** 品牌鈕上的官方 logo（白底 tile 上顯示，尺寸由 .gas-pay-brand img 控制）。 */
const WALLET_LOGOS: Record<WalletBrand, string> = {
  apple_pay: applePayLogo,
  line_pay: linePayLogo,
  ipass_money: ipassMoneyLogo,
};

/** 品牌鈕：白底 tile 放官方 logo，選中時亮邊＋右上角打勾。 */
const BrandTile = ({ brand, label, picked, onPress }: { brand: WalletBrand; label: string; picked: boolean; onPress: () => void }) => (
  <button
    type="button"
    className={`gas-pay-brand${picked ? " picked" : ""}`}
    aria-label={label}
    aria-pressed={picked}
    onClick={(e) => {
      e.stopPropagation();
      onPress();
    }}
  >
    <img src={WALLET_LOGOS[brand]} alt={label} draggable={false} />
    {picked ? (
      <span className="gas-pay-brand-check" aria-hidden>
        <RiCheckLine size={12} color="#fff" />
      </span>
    ) : null}
  </button>
);

const INK = "#0f1a35";

export type PayCatId = "card" | "wallet" | "cash";

const CAT_ORDER: PayCatId[] = ["card", "wallet", "cash"];
const LAST_PAY_CAT_KEY = "gasShop.lastPaymentCat";

export const catFromSelection = (s: PaymentSelection | null): PayCatId | null => {
  if (!s) return null;
  if (s.type === "credit_card") return "card";
  if (s.type === "electronic") return "wallet";
  if (s.type === "cash_on_delivery" || s.type === "monthly") return "cash";
  return null;
};

/** 上次選的付款類別（進頁時置中那張）；無紀錄預設電子支付。 */
export const readLastPayCat = (): PayCatId => {
  try {
    const v = localStorage.getItem(LAST_PAY_CAT_KEY);
    if (v === "card" || v === "wallet" || v === "cash") return v;
  } catch {
    /* ignore */
  }
  return "wallet";
};

export const writeLastPayCat = (c: PayCatId) => {
  try {
    localStorage.setItem(LAST_PAY_CAT_KEY, c);
  } catch {
    /* ignore */
  }
};

export const cardSelectionLabel = (c: OwnedPaymentMethod) => `信用卡 •••• ${c.card?.lastfour ?? "----"}`;

/** 指定類別置中，其餘依 card→wallet→cash 循環排上／下。 */
const orderWithCenter = (center: PayCatId): PayCatId[] => {
  const idx = CAT_ORDER.indexOf(center);
  const top = CAT_ORDER[(idx + CAT_ORDER.length - 1) % CAT_ORDER.length];
  const bottom = CAT_ORDER[(idx + 1) % CAT_ORDER.length];
  return [top, center, bottom];
};

/** 卡別徽章外框（卡面上是半透明白邊，與白底列表的版本不同）。 */
const LeadingBadge = ({ children, bg = "#fff" }: { children: ReactNode; bg?: string }) => (
  <Stack width={42} height={28} borderRadius={6} borderWidth={1} borderColor="rgba(255,255,255,0.35)" backgroundColor={bg} ai="center" jc="center">
    {children}
  </Stack>
);

/** 依 bincode 首碼判斷卡別（4=Visa、5/2=Mastercard、3=JCB，其餘泛用卡）。 */
const CardBrand = ({ bincode }: { bincode?: string }) => {
  const d = (bincode || "")[0];
  if (d === "4")
    return (
      <LeadingBadge>
        <Text fontSize={13} fontWeight="800" fontStyle="italic" color="#1a1f71" letterSpacing={-0.3}>
          VISA
        </Text>
      </LeadingBadge>
    );
  if (d === "5" || d === "2")
    return (
      <LeadingBadge>
        <XStack ai="center">
          <Stack width={13} height={13} borderRadius={7} backgroundColor="#eb001b" />
          <Stack width={13} height={13} borderRadius={7} backgroundColor="#f79e1b" ml={-6} opacity={0.92} />
        </XStack>
      </LeadingBadge>
    );
  if (d === "3")
    return (
      <LeadingBadge>
        <Text fontSize={11} fontWeight="900" color="#0b6cb3" letterSpacing={-0.2}>
          JCB
        </Text>
      </LeadingBadge>
    );
  return (
    <LeadingBadge>
      <RiBankCardLine size={19} color="#8a93a6" />
    </LeadingBadge>
  );
};

const Tag = ({ children, warn }: { children: string; warn?: boolean }) => (
  <Text className={`gas-pay-tag${warn ? " warn" : ""}`} fontSize={11} fontWeight="700">
    {children}
  </Text>
);

/** TapPay 金流安全揭露（審核要求：電子支付選項處需說明金流商與加密／PCI 認證）。深色卡面版：白字、logo 反白。 */
const WalletSecurityNote = () => (
  <p className="gas-pay-security">
    {TAPPAY_SECURITY_COPY_LEAD}
    <img src={TAPPAY_LOGO_URL} alt="TapPay" />
    {TAPPAY_SECURITY_COPY_REST}
  </p>
);

const ActBtn = ({ label, onPress, dark, picked }: { label: string; onPress: () => void; dark?: boolean; picked?: boolean }) => (
  <Stack
    className={`gas-pay-act${dark ? " dark" : ""}${picked ? " picked" : ""}`}
    onPress={(e: any) => {
      e?.stopPropagation?.();
      onPress();
    }}
    pressStyle={{ opacity: 0.8 }}
    style={{ cursor: "pointer" }}
  >
    <XStack ai="center" gap={6}>
      {picked ? <RiCheckLine size={15} color={dark ? "#fff" : INK} /> : null}
      <Text fontSize={13.5} fontWeight="800" color={dark ? "#fff" : INK}>
        {label}
      </Text>
    </XStack>
  </Stack>
);

/** 展開卡右上角關閉，取消 focus。 */
const FocusClose = ({ onClose, dark }: { onClose: () => void; dark?: boolean }) => (
  <button
    type="button"
    className={`gas-pay-focus-close${dark ? " dark" : ""}`}
    aria-label="關閉"
    onClick={(e) => {
      e.stopPropagation();
      onClose();
    }}
  >
    <RiCloseLine size={16} color={dark ? "#14532d" : "#fff"} />
  </button>
);

/** 實體感裝飾：信用卡晶片／手機 NFC／鈔票＋台幣銅板。 */
const CardChrome = ({ kind }: { kind: PayCatId }) => {
  if (kind === "card")
    return (
      <>
        <div className="gas-pay-blobs" aria-hidden>
          <span className="b1" />
          <span className="b2" />
          <span className="b3" />
        </div>
        <div className="gas-pay-chip" aria-hidden />
        <div className="gas-pay-mc" aria-hidden>
          <i />
          <i />
        </div>
      </>
    );
  if (kind === "wallet")
    return (
      <>
        {/* 橫放的手機：螢幕漸層、動態島、側鍵、NFC 波紋（機身邊框由 .c-wallet 的 inset 陰影畫） */}
        <div className="gas-pay-ph-screen" aria-hidden />
        <div className="gas-pay-ph-island" aria-hidden />
        <div className="gas-pay-ph-btn b1" aria-hidden />
        <div className="gas-pay-ph-btn b2" aria-hidden />
        <div className="gas-pay-ph-btn b3" aria-hidden />
        <div className="gas-pay-wave" aria-hidden>
          <i />
          <i />
          <i />
        </div>
      </>
    );
  return (
    <>
      {/* 鈔票：底鈔錯位、紙紋＋滿版雕版弧線、安全線、浮水印人像、外框、角落面額、流水號、銅板 */}
      <div className="gas-pay-bill-back" aria-hidden />
      <div className="gas-pay-bill-face" aria-hidden>
        <div className="gas-pay-bill-paper" />
        <div className="gas-pay-bill-guillo" />
        <div className="gas-pay-bill-stripe" />
        <div className="gas-pay-bill-portrait" />
        <div className="gas-pay-bill-frame" />
        <div className="gas-pay-bill-denom tr">50</div>
        <div className="gas-pay-bill-denom bl">50</div>
        <div className="gas-pay-bill-serial">GAS 2024050</div>
        <div className="gas-pay-coins" aria-hidden>
          <span className="coin c50">
            <span className="coin-ears" />
            <span className="coin-core">五十</span>
            <span className="coin-top">
              50<em>圓</em>
            </span>
            <span className="coin-dots" />
          </span>
          <span className="coin c1">
            <span className="coin-fret" />
            <span className="coin-one">
              壹圓<em>1</em>
            </span>
          </span>
        </div>
      </div>
    </>
  );
};

/**
 * 瓦斯訂購「付款方式」步驟：右半圓卡帶（信用卡／電子支付／現金）＋抽出展開。
 * 移植自 Kiosk 結帳引導的同一套視覺；差異：電子支付有 Apple Pay / LINE Pay 兩個品牌可選。
 * 綁卡在信用卡卡面內嵌展開，不另開 Sheet；TapPay 欄位掛載後只做視覺收合。
 * 只負責選擇與綁卡，實際扣款由後端於送達後處理。
 */
const GasPaymentOptions = ({
  isMonthly,
  selected,
  onSelect,
  onBound,
  cusJwtToken,
  cards,
  loading,
  showApplePay,
}: {
  isMonthly: boolean;
  selected: PaymentSelection | null;
  onSelect: (v: PaymentSelection | null) => void;
  /** 綁卡成功（內嵌表單）；父層負責 reload／選中。 */
  onBound: (created?: BoundCard) => void;
  cusJwtToken: string;
  cards: OwnedPaymentMethod[];
  loading: boolean;
  /** iPhone 才提供 Apple Pay；LINE Pay 一律提供。 */
  showApplePay: boolean;
}) => {
  const cashTitle = isMonthly ? "月結" : "現金";
  const cashSub = isMonthly ? "月底結算" : "送達時付現";
  const curType = selected?.type;
  const curCardId = selected?.type === "credit_card" ? selected.paymentMethodId : "";
  const curBrand = selected?.type === "electronic" ? selected.brand : null;
  const shortCardLabel = (c: OwnedPaymentMethod) => `•••• ${c.card?.lastfour ?? "----"}`;
  const brands = WALLET_BRANDS.filter((b) => b.id !== "apple_pay" || showApplePay);

  /** 進頁時凍結置中類別（當次選取不重排，下次進來才換中間那張）。 */
  const centerCat = useMemo<PayCatId>(() => catFromSelection(selected) ?? readLastPayCat(), []);
  const slotOrder = useMemo(() => orderWithCenter(centerCat), [centerCat]);
  const initialFocus = useMemo<PayCatId | null>(() => catFromSelection(selected) ?? centerCat, []);

  const [focus, setFocus] = useState<PayCatId | null>(initialFocus);
  const [popKey, setPopKey] = useState(0);
  /** 綁卡表單是否展開；一經掛載就保留 DOM。 */
  const [bindOpen, setBindOpen] = useState(false);
  const [bindMounted, setBindMounted] = useState(false);
  const stageRef = useRef<HTMLDivElement>(null);
  const [stageH, setStageH] = useState(440);

  useEffect(() => {
    const el = stageRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const apply = () => {
      const h = el.clientHeight;
      if (h > 0) setStageH(h);
    };
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  /** 槽位間距與卡片高度依 stage 比例，剛好塞進舞台、不滾動。 */
  const slotGap = Math.max(78, Math.round(stageH * 0.275));
  const collapsedH = Math.max(124, Math.round(stageH * 0.265));
  // 展開比例比 Kiosk 高一些：這頁沒有 Sheet 的固定高，舞台較矮，兩張卡＋新增鈕才放得下不必在卡內捲動
  const expandedH = Math.round(stageH * (bindOpen ? 0.82 : 0.63));

  const remember = (v: PaymentSelection | null) => {
    onSelect(v);
    const cat = catFromSelection(v);
    if (cat) writeLastPayCat(cat);
  };

  const clearCat = (id: PayCatId) => {
    if (id === "card" && curType === "credit_card") onSelect(null);
    if (id === "wallet" && curType === "electronic") onSelect(null);
    if (id === "cash" && (curType === "cash_on_delivery" || curType === "monthly")) onSelect(null);
  };

  const openBindForm = () => {
    setBindMounted(true);
    setBindOpen(true);
  };

  const collapseFocus = (id: PayCatId) => {
    setFocus(null);
    setBindOpen(false);
    clearCat(id);
  };

  const onCatPress = (id: PayCatId) => {
    if (focus === id) {
      collapseFocus(id);
      return;
    }
    setFocus(id);
    setPopKey((k) => k + 1);
    if (id !== "card") setBindOpen(false);
    if (id === "cash") {
      remember(isMonthly ? { type: "monthly" } : { type: "cash_on_delivery" });
    } else if (curType === "cash_on_delivery" || curType === "monthly") {
      onSelect(null);
    }
  };

  const fanTransform = (id: PayCatId) => {
    const slot = slotOrder.indexOf(id); // 0 上 / 1 中 / 2 下
    const on = focus === id;
    const y = slot === 0 ? -slotGap : slot === 1 ? 0 : slotGap;
    const tilt = slot === 0 ? 14 : slot === 1 ? 0 : -14;
    // 中間那張略偏左，對齊上下傾斜卡的左緣，視覺較平衡
    const xMid = slot === 1;
    if (on) {
      return `translate(${xMid ? -36 : -24}px, calc(-50% + ${Math.round(y * 0.06)}px)) rotate(0deg) scale(1.03)`;
    }
    if (focus !== null) {
      return `translate(${xMid ? 2 : 18}px, calc(-50% + ${y}px)) rotate(${tilt}deg) scale(0.86)`;
    }
    return `translate(${xMid ? -14 : 8}px, calc(-50% + ${y}px)) rotate(${tilt}deg) scale(0.96)`;
  };

  const zOf = (id: PayCatId) => {
    if (focus === id) return 6;
    const slot = slotOrder.indexOf(id);
    return slot === 1 ? 3 : slot === 0 ? 2 : 1;
  };

  const cardH = (id: PayCatId) => (focus === id ? expandedH : collapsedH);

  const cardOpen = focus === "card";
  const walletOpen = focus === "wallet";
  const cashOpen = focus === "cash";

  return (
    <YStack className="gas-pay-reel-wrap">
      <div ref={stageRef} className={`gas-pay-stage${focus ? " has-focus" : ""}${bindOpen ? " binding" : ""}`}>
        <div className="gas-pay-arc" aria-hidden />

        {/* 信用卡 */}
        <div
          className={`gas-pay-rcard c-card${cardOpen ? " on" : ""}${bindOpen && cardOpen ? " binding" : ""}`}
          style={{ transform: fanTransform("card"), zIndex: zOf("card"), height: cardH("card") }}
          onClick={() => onCatPress("card")}
          role="button"
          tabIndex={0}
        >
          <CardChrome kind="card" />
          {cardOpen && <div key={popKey} className="gas-pay-popring" aria-hidden />}
          {cardOpen && <FocusClose onClose={() => collapseFocus("card")} />}
          <div className="gas-pay-copy">
            <div className="gas-pay-toplabel">CARD</div>
            <h4>信用卡</h4>
            <div className={`gas-pay-sub${cardOpen ? " hide" : ""}`}>送達後自動扣款</div>
          </div>
          <div className="gas-pay-expand" onClick={(e) => e.stopPropagation()} aria-hidden={!cardOpen}>
            <div className="gas-pay-expand-inner">
              <div className="gas-pay-tags">
                <Tag>送達後自動扣款</Tag>
                {cards.length === 0 && !loading && !bindOpen ? <Tag warn>需先綁卡</Tag> : null}
              </div>
              {!bindOpen && <p className="gas-pay-hint">瓦斯送到後才扣款，可隨時在訂單中改付款方式。</p>}
              {loading ? (
                <XStack ai="center" gap={8} py={6}>
                  <Spinner size="small" color="#fff" />
                  <Text fontSize={12.5} color="rgba(255,255,255,0.85)">
                    載入付款方式…
                  </Text>
                </XStack>
              ) : (
                <>
                  {!bindOpen &&
                    cards.map((c) => {
                      const picked = curType === "credit_card" && curCardId === c.payment_method_id;
                      return (
                        <XStack
                          key={c.payment_method_id}
                          className={`gas-pay-cardrow${picked ? " picked" : ""}`}
                          ai="center"
                          gap={10}
                          onPress={() => remember({ type: "credit_card", paymentMethodId: c.payment_method_id, label: cardSelectionLabel(c) })}
                          pressStyle={{ opacity: 0.85 }}
                          style={{ cursor: "pointer" }}
                        >
                          <CardBrand bincode={c.card?.bincode} />
                          <YStack flex={1}>
                            <Text fontSize={14} fontWeight="800" color="#fff">
                              {shortCardLabel(c)}
                            </Text>
                            <Text fontSize={11} color="rgba(255,255,255,0.72)">
                              {c.card?.issuer || c.nickname || "信用卡"}
                            </Text>
                          </YStack>
                          {picked ? <RiCheckLine size={18} color="#fff" /> : null}
                        </XStack>
                      );
                    })}
                  {!bindOpen && <ActBtn label={cards.length === 0 ? "綁定卡片" : "＋ 新增信用卡"} onPress={openBindForm} />}
                  {bindMounted && (
                    <InlineBindCardForm
                      open={bindOpen && cardOpen}
                      cusJwtToken={cusJwtToken}
                      onCancel={() => setBindOpen(false)}
                      onBound={(created) => {
                        setBindOpen(false);
                        writeLastPayCat("card");
                        onBound(created);
                      }}
                    />
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* 電子支付 */}
        <div
          className={`gas-pay-rcard c-wallet${walletOpen ? " on" : ""}`}
          style={{ transform: fanTransform("wallet"), zIndex: zOf("wallet"), height: cardH("wallet") }}
          onClick={() => onCatPress("wallet")}
          role="button"
          tabIndex={0}
        >
          <CardChrome kind="wallet" />
          {walletOpen && <div key={popKey} className="gas-pay-popring" aria-hidden />}
          {walletOpen && <FocusClose onClose={() => collapseFocus("wallet")} />}
          <div className="gas-pay-copy">
            <div className="gas-pay-toplabel">PAY</div>
            <h4>電子支付</h4>
            <div className={`gas-pay-sub gas-pay-brandpills${walletOpen ? " hide" : ""}`}>
              {brands.map((b) => (
                <span key={b.id} className={`gas-pay-pill${b.id === "ipass_money" ? " ipass" : ""}`}>
                  <img src={WALLET_LOGOS[b.id]} alt={b.label} draggable={false} />
                </span>
              ))}
            </div>
          </div>
          <div className="gas-pay-expand" onClick={(e) => e.stopPropagation()} aria-hidden={!walletOpen}>
            <div className="gas-pay-expand-inner">
              <div className="gas-pay-tags">
                <Tag>送達後掃碼付款</Tag>
                <Tag>免輸入卡號</Tag>
              </div>
              <p className="gas-pay-hint">送達時配送員出示付款碼，你掃碼確認後才扣款；這裡先選要用的 App。</p>
              <div className="gas-pay-brandrow">
                {brands.map((b) => (
                  <BrandTile
                    key={b.id}
                    brand={b.id}
                    label={b.label}
                    picked={curBrand === b.id}
                    onPress={() => remember({ type: "electronic", brand: b.id, label: b.label })}
                  />
                ))}
              </div>
              {curBrand && (
                <XStack ai="center" gap={6} mt={2}>
                  <RiCheckLine size={16} color="#fff" />
                  <Text fontSize={12.5} fontWeight="700" color="rgba(255,255,255,0.92)">
                    已選擇 {WALLET_BRANDS.find((b) => b.id === curBrand)?.label}
                  </Text>
                </XStack>
              )}
              <WalletSecurityNote />
            </div>
          </div>
        </div>

        {/* 現金／月結 */}
        <div
          className={`gas-pay-rcard c-cash${cashOpen ? " on" : ""}`}
          style={{ transform: fanTransform("cash"), zIndex: zOf("cash"), height: cardH("cash") }}
          onClick={() => onCatPress("cash")}
          role="button"
          tabIndex={0}
        >
          <CardChrome kind="cash" />
          {cashOpen && <div key={popKey} className="gas-pay-popring" aria-hidden />}
          {cashOpen && <FocusClose onClose={() => collapseFocus("cash")} dark />}
          <div className="gas-pay-copy">
            <div className="gas-pay-toplabel">{isMonthly ? "MONTHLY" : "CASH"}</div>
            <h4>{cashTitle}</h4>
            <div className={`gas-pay-sub${cashOpen ? " hide" : ""}`}>{cashSub}</div>
          </div>
          <div className="gas-pay-expand" aria-hidden={!cashOpen}>
            <div className="gas-pay-expand-inner">
              <div className="gas-pay-tags">{isMonthly ? <Tag>月底結算</Tag> : <Tag>當面付現</Tag>}</div>
              <p className="gas-pay-hint cash">
                {isMonthly ? "本月訂單彙總後再結算，無需當場付款。" : "配送員送達時收取現金，建議備妥零錢以便找零。"}
              </p>
            </div>
          </div>
        </div>
      </div>
    </YStack>
  );
};

export default GasPaymentOptions;
